import { useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { useDebounce } from "use-debounce";

import { cn } from "@/libs/cn";
import { useSessionStore } from "@/modules/auth/store/useSessionStore";
import Button from "@/modules/common/components/files/Button";
import DeleteModal from "@/modules/common/components/files/DeleteModal";
import EntityGridList from "@/modules/common/components/files/EntityGridList";
import { GridTableCell } from "@/modules/common/components/files/GridTable";
import Input from "@/modules/common/components/files/Input";
import { Modal, ModalBody } from "@/modules/common/components/files/Modal";
import PageBreadcrumb from "@/modules/common/components/files/PageBreadcrumb";
import SelectV2 from "@/modules/common/components/files/SelectV2";
import Spinner from "@/modules/common/components/files/Spinner";
import Tag from "@/modules/common/components/files/Tag";
import { usePagination } from "@/modules/common/hooks/usePagination";
import { AdminUserRole } from "@/modules/common/types/Users.enum";
import { getApiErrorMessage } from "@/modules/common/utils/apiError";
import { formatDate } from "@/modules/common/utils/format";
import { ROUTES } from "@/routes/constants/routes";
import { ADMIN_RELEASE_NOTES_ENDPOINTS, AdminReleaseNotesService } from "@/service/api/admin-release-notes";
import { ReleaseNoteListItemResponse } from "@/service/response/admin-release-notes";

import MarkdownPreview from "../../knowledge-base/components/MarkdownPreview";
import ReleaseNoteActions from "../components/ReleaseNoteActions";
import {
  RELEASE_NOTE_AUDIENCE_OPTIONS,
  RELEASE_NOTE_STATUS_OPTIONS,
  releaseNoteAudienceLabel,
  releaseNoteStatusLabel,
  releaseNoteStatusVariant,
} from "../constants";

const gridCols = "md:grid-cols-[1fr_3fr_1.4fr_1.3fr_1.3fr_1.1fr_0.6fr]";
const gridGap = "md:gap-4";
const gridLayout = cn("flex flex-col gap-2 md:grid md:items-center", gridCols, gridGap, "px-4 py-4 md:px-6");

const ReleaseNoteList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canUserSee } = useSessionStore();
  const canWrite = canUserSee(AdminUserRole.ADMIN);
  const { page, setPage, limit, setPageSize, resetPage } = usePagination();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 400);
  const [audiences, setAudiences] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [viewingId, setViewingId] = useState<string>();
  const [deleting, setDeleting] = useState<ReleaseNoteListItemResponse>();
  const [deletePending, setDeletePending] = useState(false);

  const query = useQuery({
    queryKey: [ADMIN_RELEASE_NOTES_ENDPOINTS.LIST, { page, limit, search: debouncedSearch, audiences, statuses }],
    queryFn: () =>
      AdminReleaseNotesService.list({
        page,
        limit,
        search: debouncedSearch.trim() || undefined,
        audiences: audiences.map(Number),
        statuses: statuses.map(Number),
        sortBy: "createdAt",
        sortOrder: "desc",
      }),
  });

  const viewing = useQuery({
    queryKey: [ADMIN_RELEASE_NOTES_ENDPOINTS.LIST, viewingId],
    queryFn: () => AdminReleaseNotesService.get(viewingId!),
    enabled: Boolean(viewingId),
  });

  const changeFilter = (setter: (value: string[]) => void) => (value: string[]) => {
    setter(value);
    resetPage();
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeletePending(true);
    try {
      await AdminReleaseNotesService.remove(deleting.id);
      toast.success("Nota excluída.");
      setDeleting(undefined);
      await queryClient.invalidateQueries({ queryKey: [ADMIN_RELEASE_NOTES_ENDPOINTS.LIST] });
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Não foi possível excluir a nota."));
    } finally {
      setDeletePending(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <PageBreadcrumb items={[{ label: "Notas de Atualização" }]} />

      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold text-ui-primary">Notas de Atualização</h1>
          <p className="mt-1 text-sm text-ui-text-muted">Escreva e publique atualizações da plataforma para os usuários.</p>
        </div>
        {canWrite && <Button color="primary" icon={<Plus size={16} />} label="Nova nota" route={ROUTES.RELEASE_NOTES.ADD} />}
      </header>

      <section className="grid gap-3 rounded-2xl border border-ui-border bg-white p-4 md:grid-cols-[2fr_1fr_1fr]">
        <Input
          icon={<Search size={16} />}
          placeholder="Pesquisar por título ou versão..."
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            resetPage();
          }}
        />
        <SelectV2
          options={RELEASE_NOTE_AUDIENCE_OPTIONS}
          placeholder="Todos os públicos"
          value={audiences}
          onChange={changeFilter(setAudiences)}
        />
        <SelectV2
          options={RELEASE_NOTE_STATUS_OPTIONS}
          placeholder="Todos os status"
          value={statuses}
          onChange={changeFilter(setStatuses)}
        />
      </section>

      <EntityGridList
        data={query.data}
        isLoading={query.isLoading}
        isFetching={query.isFetching}
        limit={limit}
        minWidthClassName="min-w-[1000px]"
        onChangePage={setPage}
        onChangePageSize={setPageSize}
        renderHeader={() => (
          <div className={cn("hidden border-b border-gray-100 bg-white md:grid md:items-center", gridCols, gridGap, "px-6 py-4")}>
            {["Versão", "Nota", "Público", "Autor", "Publicada em", "Status", "Ações"].map((label) => (
              <span key={label} className="truncate text-sm font-semibold text-gray-500">
                {label}
              </span>
            ))}
          </div>
        )}
        renderSkeleton={(rows) => (
          <div className="rounded-lg bg-white p-6 shadow">
            {Array.from({ length: rows }).map((_, index) => (
              <div key={index} className="mb-3 h-10 animate-pulse rounded-lg bg-gray-100" />
            ))}
          </div>
        )}
        renderRow={(item) => (
          <ReleaseNoteRow
            item={item}
            canWrite={canWrite}
            onOpen={() => navigate(generatePath(ROUTES.RELEASE_NOTES.EDIT, { id: item.id }))}
            onView={() => setViewingId(item.id)}
            onDelete={() => setDeleting(item)}
          />
        )}
      />

      <Modal
        open={Boolean(viewingId)}
        onOpenChange={(open) => !open && setViewingId(undefined)}
        title={viewing.data ? `${viewing.data.title} · ${viewing.data.version}` : "Nota de atualização"}
        className="max-w-3xl"
      >
        <ModalBody className="mt-4 max-h-[65vh] overflow-y-auto">
          {viewing.isLoading ? (
            <div className="flex min-h-40 items-center justify-center">
              <Spinner />
            </div>
          ) : viewing.isError ? (
            <p className="text-sm text-ui-text-muted">Não foi possível carregar esta nota.</p>
          ) : (
            <MarkdownPreview markdown={viewing.data?.renderedContent ?? ""} onMediaError={() => void viewing.refetch()} />
          )}
        </ModalBody>
      </Modal>

      <DeleteModal
        open={Boolean(deleting)}
        title="Excluir nota"
        description={
          deleting ? `A nota ${deleting.version} some da lista e dos portais. O número da versão não é reaproveitado.` : undefined
        }
        confirmText="Excluir"
        loading={deletePending}
        onClose={() => setDeleting(undefined)}
        onDelete={() => void confirmDelete()}
      />
    </div>
  );
};

interface ReleaseNoteRowProps {
  item: ReleaseNoteListItemResponse;
  canWrite: boolean;
  onOpen: () => void;
  onView: () => void;
  onDelete: () => void;
}

const ReleaseNoteRow = ({ item, canWrite, onOpen, onView, onDelete }: ReleaseNoteRowProps) => (
  <div
    role="link"
    tabIndex={0}
    onClick={onOpen}
    onKeyDown={(event) => event.key === "Enter" && onOpen()}
    className={cn(
      gridLayout,
      "cursor-pointer rounded-xl border border-gray-100 bg-white transition hover:bg-gray-50 md:rounded-none md:border-0 md:border-b",
    )}
  >
    <GridTableCell label="Versão">
      <span className="font-medium text-ui-primary">{item.version}</span>
    </GridTableCell>
    <GridTableCell label="Nota">{item.title}</GridTableCell>
    <GridTableCell label="Público">{releaseNoteAudienceLabel(item.audiences)}</GridTableCell>
    <GridTableCell label="Autor">{item.createdBy.name}</GridTableCell>
    <GridTableCell label="Publicada em">{item.publishedAt ? formatDate(item.publishedAt, "DD/MM/YYYY") : "—"}</GridTableCell>
    <GridTableCell label="Status">
      <Tag label={releaseNoteStatusLabel(item.status)} variant={releaseNoteStatusVariant(item.status)} size="small" />
    </GridTableCell>
    <GridTableCell label="Ações">
      <ReleaseNoteActions id={item.id} canWrite={canWrite} onView={onView} onDelete={onDelete} />
    </GridTableCell>
  </div>
);

export default ReleaseNoteList;
